'use client'

/**
 * Redux (`pdpStateManager`) → PL store bridge: the legacy bundle buy box exposes no selection callback
 * but already publishes to redux, so mirror that. First child's options drive reco refetches; all
 * required children (add-ons excluded) drive transparent pricing / brand comparison — non-PL semantics.
 */

import { useEffect, useMemo } from 'react'

import { useSetProductSelection } from '@src/components/pl-base-widget/contexts/page-widget-store'
import {
  useSetPdpSelectionDerived,
  type PdpBundleSelection,
} from '@src/components/pl-base-widget/contexts/pdp-selection'
import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import type Product from '@src/quince-site-common/src/domains/Product'
import type Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import type { IProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import formatDisplayAmountValue from '@src/quince-site-common/src/utils/formatDisplayAmountValue'
import { getProductPriceMap } from '@src/quince-site-pdp/src/organisms/productPageSections/bundleProductPurchase/BundleProductPurchase.helpers'

/** Shape `pdpStateManager.bundleSelectedOptions` carries (see `PDPStateManager.ts:24-27`). */
interface BundleSelectedOption {
  product: Product
  selectedOptions: IProductVariantOption[]
}

export function useBundleSelectionBridge(bundle: Bundle | undefined): void {
  const publishSelection = useSetProductSelection()
  const publishDerived = useSetPdpSelectionDerived()

  // First required child's options — the reco refetch signal.
  const firstChildOptions = useAppSelector<IProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)
  // All required children — the transparent-pricing / brand-comparison signal.
  const bundleSelectedOptions = useAppSelector<BundleSelectedOption[]>(
    pdpStateManager,
    pdpStateManager.getBundleSelectedOptions,
  )

  // Value-keyed (not array identity): redux republishes a new array on every click, so only a genuine
  // first-child change should re-publish and churn the fetcher.
  const firstChildKey = useMemo(
    () => firstChildOptions.map(option => `${option.name}=${option.value}`).join('&'),
    [firstChildOptions],
  )

  useEffect(() => {
    // Empty pre-seed publish would flip the authoritative gate and let recos fetch colourless.
    if (!firstChildKey) return
    publishSelection(firstChildOptions.map(option => ({ name: option.name, value: option.value })))
    // `firstChildKey` is `firstChildOptions`'s value-identity; the raw array would re-run every republish.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstChildKey, publishSelection])

  // Bundle totals + per-child variant identity. Price math lifted from non-PL
  // `TransparentPricingBundle.tsx:30-38`: sum child sale prices, then apply the bundle discount.
  const derived = useMemo(() => {
    if (!bundle || bundleSelectedOptions.length === 0) return undefined

    const prices = getProductPriceMap(bundleSelectedOptions)
    const productsSumPrice = formatDisplayAmountValue(
      prices.reduce((sum, { pricing }) => sum + parseFloat(pricing.formattedSalePrice), 0),
    )
    const traditionalPrice = formatDisplayAmountValue(
      prices.reduce((sum, { pricing }) => sum + parseFloat(pricing.formattedTraditionalPrice ?? '0'), 0),
    )

    const bundleSelections: PdpBundleSelection[] = bundleSelectedOptions.map(({ product, selectedOptions }) => {
      const variants = productHelper.getVariantsForOptions(product, selectedOptions)
      // Two ids per child — non-PL resolves per section: transparent pricing takes `[0]`,
      // brand comparison takes the cheapest match (same math as `productHelper.getBrandComparison`).
      const minPrice = Math.min(...variants.map(variant => variant.price))
      return {
        productId: String(product.id),
        variantId: variants[0]?.id,
        minPriceVariantId: variants.find(variant => variant.price === minPrice)?.id,
      }
    })

    return {
      quincePrice: Number(getDiscountedPrice(Number(productsSumPrice), bundle.discountPrice ?? 0)),
      traditionalPrice: Number(traditionalPrice) || 0,
      bundleSelections,
      // First required child's numeric id — non-PL's bundle substitute (`getFirstProductIdForBundle`);
      // read live so a first-child sibling swap updates it.
      recoProductId: bundleSelections[0]?.productId,
      // First required child's slug — PRODUCT_GALLERY targets the image experiment with it, matching
      // non-PL bundles (`bundle-product-images-legacy.tsx:30` targets the first child's slug).
      recoSlug: bundleSelectedOptions[0]?.product.slug,
    }
  }, [bundle, bundleSelectedOptions])

  useEffect(() => {
    if (!derived) return
    publishDerived(derived)
  }, [derived, publishDerived])
}
